import React, { useCallback, useEffect, useRef, useState } from "react";
import { Socket } from "socket.io-client";
import { useAccount, useToast } from "../context";
import { ConversationData, MessageType } from "../interfaces/all";
import {
  getUnreadMessagesCount,
  markAllMessagesAsRead,
  markMessageAsRead,
  markMessageAsUnread,
} from "../services/api";
import { useDebounce } from "./useDebounce";

interface UseMessageReadStatusProps {
  socket: React.RefObject<Socket | null>;
  selectedConversation: ConversationData | null;
  onMessageUpdate?: (message: MessageType) => void;
}

export interface ReadStatus {
  isRead: boolean;
  readBy?: {
    id: string;
    name: string;
  };
  readAt?: Date;
}

export const useMessageReadStatus = ({
  socket,
  selectedConversation,
  onMessageUpdate,
}: UseMessageReadStatusProps) => {
  const { account } = useAccount();
  const { showError } = useToast();

  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const messageReadStatusRef = useRef<Map<string, ReadStatus>>(new Map());
  const messagesRef = useRef<MessageType[]>([]);
  const processingMessagesRef = useRef<Set<string>>(new Set()); // Para evitar processamento duplicado
  const MAX_READ_STATUS_ENTRIES = 2000;

  // Efeito para sincronizar o status quando a conversa muda (NÃO limpar o ref)
  useEffect(() => {
    if (selectedConversation) {
      // Em vez de limpar, vamos sincronizar o status das mensagens existentes
      messagesRef.current.forEach((message) => {
        if (message._id && message.readStatus) {
          messageReadStatusRef.current.set(message._id, message.readStatus);
        }
      });
    }
  }, [selectedConversation?.id, selectedConversation]); // Adicionada dependência selectedConversation

  // Efeito adicional para garantir que o hook seja inicializado corretamente
  useEffect(() => {
    if (selectedConversation && messagesRef.current.length > 0) {
      // Forçar sincronização de todas as mensagens
      messagesRef.current.forEach((message) => {
        if (message._id) {
          if (message.readStatus) {
            messageReadStatusRef.current.set(message._id, message.readStatus);
          } else if (message.sentBy === "client") {
            messageReadStatusRef.current.set(message._id, { isRead: false });
          }
        }
      });
    }
  }, [selectedConversation?.id, selectedConversation]); // Adicionada dependência selectedConversation

  // Função para atualizar o status de leitura de uma mensagem
  const updateMessageReadStatus = useCallback(
    (messageId: string, status: ReadStatus) => {
      // Evita atualizações desnecessárias
      const currentStatus = messageReadStatusRef.current.get(messageId);

      if (currentStatus?.isRead === status.isRead) {
        return;
      }

      messageReadStatusRef.current.set(messageId, status);

      // Limitar tamanho do Map para evitar vazamento de memória
      if (messageReadStatusRef.current.size > MAX_READ_STATUS_ENTRIES) {
        const firstKey = messageReadStatusRef.current.keys().next().value;
        if (firstKey) {
          messageReadStatusRef.current.delete(firstKey);
        }
      }

      if (onMessageUpdate) {
        // Usar requestAnimationFrame para garantir atualização imediata
        requestAnimationFrame(() => {
          onMessageUpdate({
            _id: messageId,
            readStatus: status,
          } as MessageType);
        });
      }
    },
    [onMessageUpdate],
  );

  // Função para verificar se uma mensagem existe
  const messageExists = useCallback((messageId: string) => {
    const message = messagesRef.current.find((m) => m._id === messageId);
    return !!message;
  }, []);

  // Função para marcar uma mensagem como lida
  const markAsRead = useCallback(
    async (messageId: string) => {
      // Evita processamento duplicado
      if (processingMessagesRef.current.has(messageId)) {
        return;
      }

      if (!account || !selectedConversation) {
        return;
      }

      if (!messageExists(messageId)) {
        return;
      }

      // Verificar se a mensagem pertence à conversa selecionada
      const message = messagesRef.current.find((m) => m._id === messageId);
      if (!message) {
        return;
      }

      const conversationPhone =
        selectedConversation.valid_telephone || selectedConversation.telephone;
      const messagePhone = message.to || message.from;

      // Só marcar como lida se a mensagem pertence à conversa selecionada
      if (messagePhone !== conversationPhone) {
        return;
      }

      // Marca como sendo processada
      processingMessagesRef.current.add(messageId);

      try {
        await markMessageAsRead(messageId, account.id, account.username);

        updateMessageReadStatus(messageId, {
          isRead: true,
          readBy: {
            id: account.id,
            name: account.username,
          },
          readAt: new Date(),
        });
      } catch (error: any) {
        // Se a mensagem não foi encontrada, apenas atualiza o status localmente
        if (
          error.response?.status === 400 &&
          error.response?.data?.message?.includes("não foi encontrada")
        ) {
          updateMessageReadStatus(messageId, {
            isRead: true,
            readBy: {
              id: account.id,
              name: account.username,
            },
            readAt: new Date(),
          });
          return;
        }
        showError(error);
      } finally {
        // Remove do conjunto de processamento
        processingMessagesRef.current.delete(messageId);
      }
    },
    [
      account,
      selectedConversation,
      showError,
      updateMessageReadStatus,
      messageExists,
    ],
  );

  // Função para marcar uma mensagem como não lida
  const markAsUnread = useCallback(
    async (messageId: string) => {
      try {
        await markMessageAsUnread(messageId);
        updateMessageReadStatus(messageId, {
          isRead: false,
        });
      } catch (error) {
        showError(error);
      }
    },
    [showError, updateMessageReadStatus],
  );

  // Função para marcar todas as mensagens como lidas
  const markAllAsRead = useCallback(async () => {
    if (!account || !selectedConversation) {
      return;
    }

    setIsLoading(true);
    try {
      const phoneNumber =
        (selectedConversation as ConversationData).valid_telephone ||
        selectedConversation.telephone;

      await markAllMessagesAsRead(phoneNumber, account.id, account.username);

      // Atualizar apenas mensagens do cliente que não estão lidas no estado local
      const now = new Date();
      let hasChanges = false;

      messagesRef.current.forEach((message) => {
        if (
          message._id &&
          message.sentBy === "client" &&
          !messageReadStatusRef.current.get(message._id)?.isRead
        ) {
          messageReadStatusRef.current.set(message._id, {
            isRead: true,
            readBy: {
              id: account.id,
              name: account.username,
            },
            readAt: now,
          });
          hasChanges = true;
        }
      });

      if (hasChanges) {
        // O estado visual (messages) será atualizado pelo listener de 'allMessagesRead'
        // em ChatMessages.tsx, que já faz o update em lote (batch) de forma eficiente.
      }
      setUnreadCount(0);
      // Disparar evento customizado para notificar outros componentes
      const event = new CustomEvent("allMessagesRead", {
        detail: { phoneNumber },
      });
      window.dispatchEvent(event);

      // Disparar evento via socket também para garantir sincronização
      if (socket?.current) {
        socket.current.emit("allMessagesRead", { phoneNumber, count: 0 });
      }
    } catch (error) {
      console.error("Erro ao marcar mensagens como lidas:", error);
      showError(error);
    } finally {
      setIsLoading(false);
    }
  }, [
    account,
    selectedConversation,
    showError,
    updateMessageReadStatus,
    socket,
  ]);

  // Função para atualizar o contador de mensagens não lidas
  const updateUnreadCount = useCallback(async () => {
    if (!selectedConversation) return;

    try {
      const phoneNumber =
        (selectedConversation as ConversationData).valid_telephone ||
        selectedConversation.telephone;
      const response = await getUnreadMessagesCount(phoneNumber);
      if (response.status === "success" && typeof response.data === "number") {
        setUnreadCount(response.data);
      }
    } catch (error) {
      showError(error);
    }
  }, [selectedConversation, showError]);

  // Debounce reduzido para marcar mensagens como lidas
  const debouncedMarkAsRead = useDebounce(markAsRead, 300);

  // Cleanup do debounce quando o componente for desmontado
  useEffect(() => {
    return () => {
      if (
        "cancel" in debouncedMarkAsRead &&
        typeof debouncedMarkAsRead.cancel === "function"
      ) {
        debouncedMarkAsRead.cancel();
      }
    };
  }, [debouncedMarkAsRead]);

  // Efeito para carregar o contador inicial de mensagens não lidas
  useEffect(() => {
    if (selectedConversation) {
      updateUnreadCount();
    }
  }, [selectedConversation, updateUnreadCount]);

  // Efeito para configurar os listeners de socket
  useEffect(() => {
    const currentSocket = socket.current;
    if (!currentSocket) return;

    const handleMessageReadStatusUpdate = (data: {
      messageId: string;
      to: string;
      isRead: boolean;
      readBy?: {
        id: string;
        name: string;
      };
      readAt?: Date;
    }) => {
      const conversationPhone =
        selectedConversation?.valid_telephone ||
        selectedConversation?.telephone;
      if (conversationPhone === data.to) {
        // Atualizar imediatamente para evitar delay
        updateMessageReadStatus(data.messageId, {
          isRead: data.isRead,
          readBy: data.readBy,
          readAt: data.readAt ? new Date(data.readAt) : undefined,
        });
        // Atualizar contador em background para não bloquear a UI
        setTimeout(() => {
          updateUnreadCount();
        }, 0);
      }
    };

    const handleAllMessagesRead = (data: {
      phoneNumber: string;
      readBy: {
        id: string;
        name: string;
      };
      readAt: Date;
      count: number;
    }) => {
      const conversationPhone =
        selectedConversation?.valid_telephone ||
        selectedConversation?.telephone;
      if (conversationPhone === data.phoneNumber) {
        setUnreadCount(0);

        // Atualizar todas as mensagens não lidas localmente de forma otimizada
        const unreadMessages = messagesRef.current.filter(
          (message) =>
            message.sentBy === "client" &&
            message._id &&
            !message.readStatus?.isRead,
        );

        // Usar requestAnimationFrame para atualizar em batch
        requestAnimationFrame(() => {
          unreadMessages.forEach((message) => {
            if (message._id) {
              updateMessageReadStatus(message._id, {
                isRead: true,
                readBy: data.readBy,
                readAt: data.readAt,
              });
            }
          });
        });

        // Disparar evento customizado para notificar outros componentes
        const event = new CustomEvent("allMessagesRead", {
          detail: { phoneNumber: data.phoneNumber },
        });
        window.dispatchEvent(event);
      }
    };

    currentSocket.on("messageReadStatusUpdate", handleMessageReadStatusUpdate);
    currentSocket.on("allMessagesRead", handleAllMessagesRead);

    return () => {
      currentSocket.off(
        "messageReadStatusUpdate",
        handleMessageReadStatusUpdate,
      );
      currentSocket.off("allMessagesRead", handleAllMessagesRead);
    };
  }, [
    socket,
    selectedConversation,
    updateMessageReadStatus,
    updateUnreadCount,
  ]);

  // Função para atualizar as mensagens
  const updateMessages = useCallback((messages: MessageType[]) => {
    messagesRef.current = messages;

    // Sincronizar o status de leitura para todas as mensagens de forma otimizada
    requestAnimationFrame(() => {
      messages.forEach((message) => {
        if (message._id) {
          // Sempre sincronize o status do ref com o status da mensagem
          if (message.readStatus) {
            messageReadStatusRef.current.set(message._id, message.readStatus);
          } else if (!messageReadStatusRef.current.has(message._id)) {
            // Se não tem status, inicialize como não lida para mensagens do cliente
            if (message.sentBy === "client") {
              messageReadStatusRef.current.set(message._id, { isRead: false });
            }
          }
        }
      });
    });
  }, []);

  // Função para obter o status de leitura atualizado de uma mensagem
  const getMessageReadStatus = useCallback((messageId: string) => {
    // Primeiro, verificar no ref
    const status = messageReadStatusRef.current.get(messageId);

    if (status) {
      return status;
    }

    // Se não encontrou no ref, procurar na mensagem atual
    const message = messagesRef.current.find((m) => m._id === messageId);

    if (message?.readStatus) {
      // Atualizar o ref com o status da mensagem
      messageReadStatusRef.current.set(messageId, message.readStatus);
      return message.readStatus;
    }

    // Se é uma mensagem do cliente sem status, retornar como não lida
    if (message?.sentBy === "client") {
      const defaultStatus = { isRead: false };
      messageReadStatusRef.current.set(messageId, defaultStatus);
      return defaultStatus;
    }

    // Se não encontrou nada, retornar undefined para evitar skeleton desnecessário
    return undefined;
  }, []);

  return {
    unreadCount,
    isLoading,
    markAsRead: debouncedMarkAsRead,
    markAsReadDirect: markAsRead, // Função direta sem debounce
    markAsUnread,
    markAllAsRead,
    updateUnreadCount,
    getMessageReadStatus,
    updateMessages,
    updateMessageReadStatus,
  };
};
